import { type AxiosResponse } from 'axios'
import { type AxiosOptions } from 'shared/api'
import type { IParamsAssignInbox, IResponseNumber } from 'entities/Phone/api/types'
import { baseApi } from 'entities/Auth'
import { type IResponseBusinessHours, type IResponseUser } from 'entities/Users/api/types'
import { type ICallSettingsItem } from 'entities/Organization/api/types'
import { type IResponseTeamPayload } from 'entities/Teams/api/types'
import {
  type IOptionsGetTeamById,
  type IParamsAssignAircallNumbersToInbox,
  type IParamsAssignAircallNumbersToInboxPerNumber,
  type IParamsAssignNumbersToInbox,
  type IParamsChangeInboxOwner,
  type IParamsCreateCoreGroup,
  type IParamsCreateInbox,
  type IParamsCreateIntegrationGroup,
  type IParamsCreateTeamsGroupsByIdAddTeams,
  type IParamsDeleteInbox,
  type IParamsPersonal,
  type IParamsTeamsAll,
  type IParamsTeamsFavorite,
  type IParamsTeamsGroupsCreate,
  type IParamsTeamsGroupsUpdate,
  type IParamsTeamsMuted,
  type IParamsTeamsSearch,
  type IParamsUpdateInboxAiAgent,
  type IParamsUpdateInboxBusinessHours,
  type IParamsUpdateInboxCallSettings,
  type IParamsUpdateInboxesEmail,
  type IParamsUpdateTeamByConversationAssignment,
  type IParamsUpdateTeamsByColorCoding,
  type IParamsUpdateTeamsByHistory,
  type IParamsUpdateTeamsById,
  type IParamsUpdateTeamsByIdMembers,
  type IResponseAddMembersToExistingInbox,
  type IResponseAssignAircallNumbersToInboxPerNumber,
  type IResponseCreateTeamsGroupsByIdAddTeams,
  type IResponseDeleteMembersBulk,
  type IResponseGetTeamByIdMembers2_0,
  type IResponseGetTeamsByIdQueueBySid,
  type IResponseHubspotAssignmentFields,
  type IResponseInbox,
  type IResponseInboxConversationAssignment,
  type IResponseInboxesSearch,
  type IResponseInboxGroup,
  type IResponseIntegrationTeam,
  type IResponseMakeDefaultInbox,
  type IResponseTeamsFavorite,
  type IResponseTeamsGroupsByIdRemoveTeamById,
  type IResponseTeamsMuted,
  type IResponseUnifiedInbox,
  type IResponseUpdateInboxesEmail,
  type IResponseUpdateTeamsByIdMembers,
} from './types'

class Api {
  getGroupInboxes(): Promise<AxiosResponse<IResponseInboxGroup[]>> {
    return baseApi.get('teams/groups/personal').then((response) => {
      if (response.data.data) {
        response.data = response.data.data
      }

      return response
    })
  }

  getInboxesPersonal(
    data: IParamsPersonal = {}
  ): Promise<AxiosResponse<Array<IResponseInbox | IResponseUnifiedInbox>>> {
    return baseApi.get('teams/channels/personal', { params: data })
  }

  updateTeamsFavorite(data: IParamsTeamsFavorite): Promise<AxiosResponse<IResponseTeamsFavorite>> {
    return baseApi.post('teams/favorite', data)
  }

  createTeamsGroupsByIdAddTeams(
    id: number,
    data: IParamsCreateTeamsGroupsByIdAddTeams
  ): Promise<AxiosResponse<IResponseCreateTeamsGroupsByIdAddTeams>> {
    return baseApi.post(`teams/groups/${id}/add-teams`, data)
  }

  deleteTeamsGroupsByIdRemoveTeamById(
    groupId: number,
    id: number
  ): Promise<AxiosResponse<IResponseTeamsGroupsByIdRemoveTeamById>> {
    return baseApi.delete(`teams/groups/${groupId}/remove-team/${id}`)
  }

  updateTeamsGroupsCreate(
    data: IParamsTeamsGroupsCreate
  ): Promise<AxiosResponse<IResponseInboxGroup>> {
    return baseApi.post('teams/groups/create', data)
  }

  createCoreGroup(data: IParamsCreateCoreGroup): Promise<AxiosResponse<IResponseInboxGroup>> {
    return baseApi.post('groups', data)
  }

  deleteTeamsGroupsById(id: number): Promise<AxiosResponse<null>> {
    return baseApi.delete(`teams/groups/${id}`)
  }

  updateTeamsGroupsById(
    id: number,
    body: IParamsTeamsGroupsUpdate
  ): Promise<AxiosResponse<IResponseInboxGroup>> {
    return baseApi.post(`teams/groups/${id}`, body)
  }

  getTeamsSearch(
    params: IParamsTeamsSearch,
    options?: AxiosOptions
  ): Promise<AxiosResponse<IResponseInboxesSearch>> {
    return baseApi.get('teams/channels/search', { ...options, params })
  }

  getTeamById(id: number, options?: IOptionsGetTeamById): Promise<AxiosResponse<IResponseInbox>> {
    return baseApi.get(`teams/${id}`, options)
  }

  updateTeamsById(
    id: number,
    params: IParamsUpdateTeamsById
  ): Promise<AxiosResponse<IResponseInbox>> {
    return baseApi.post(`teams/${id}`, params)
  }

  getTeamsAll(
    params?: IParamsTeamsAll,
    options?: AxiosOptions
  ): Promise<AxiosResponse<IResponseInbox[]>> {
    return baseApi.get('/teams/channels/all', { params, ...options })
  }

  getIntegrationTeams(): Promise<AxiosResponse<IResponseIntegrationTeam[]>> {
    return baseApi.get('groups/integration-teams')
  }

  createIntegrationGroup(
    params: IParamsCreateIntegrationGroup
  ): Promise<AxiosResponse<IResponseInboxGroup>> {
    return baseApi.post('groups/create-integration-group', params)
  }

  importIntegrationTeams(): Promise<AxiosResponse<unknown>> {
    return baseApi.get('groups/import-integration-teams')
  }

  refreshIntegrationTeams(teamId: number): Promise<AxiosResponse<unknown>> {
    return baseApi.get('groups/refresh-integration-teams', {
      // Note. Back end API endpoint expects integration_team_id, but we pass teamId here.
      params: { integration_team_id: teamId },
    })
  }

  refreshIntegrationTeamsList(): Promise<AxiosResponse<unknown>> {
    return baseApi.get('groups/refresh-integration-teams')
  }

  refreshIntegrationTeamContacts(teamId: number): Promise<AxiosResponse<unknown>> {
    return baseApi.get(`groups/refresh-integration-team-contacts/${teamId}`)
  }

  updateGroup(params: {
    group_id: number
    name: string
    description?: string | null
    members: number[]
  }): Promise<AxiosResponse<IResponseTeamPayload>> {
    return baseApi.put('groups/update', params)
  }

  patchGroupMembersAdd(
    groupId: number,
    params: { members: number[] }
  ): Promise<AxiosResponse<IResponseTeamPayload>> {
    return baseApi.patch(`groups/${groupId}/members/add`, params)
  }

  patchGroupMembersRemove(
    groupId: number,
    params: { members: number[] }
  ): Promise<AxiosResponse<IResponseTeamPayload>> {
    return baseApi.patch(`groups/${groupId}/members/remove`, params)
  }

  assignNumbersToInbox(
    params: IParamsAssignNumbersToInbox
  ): Promise<AxiosResponse<IResponseInbox>> {
    return baseApi.post('teams/bulk', params)
  }

  assignAircallNumbersToInbox(
    id: number,
    params: IParamsAssignAircallNumbersToInbox
  ): Promise<AxiosResponse<IResponseInbox>> {
    return baseApi.post(`teams/${id}/numbers/bulk`, params)
  }

  getInboxNumbers(id: number): Promise<AxiosResponse<IResponseNumber[]>> {
    return baseApi.get(`teams/${id}/numbers`)
  }

  assignAircallNumbersToInboxPerNumber(
    params: IParamsAssignAircallNumbersToInboxPerNumber
  ): Promise<AxiosResponse<IResponseAssignAircallNumbersToInboxPerNumber>> {
    return baseApi.post('teams/per-number/bulk', params)
  }

  updateTeamsMute(
    id: number,
    params: IParamsTeamsMuted
  ): Promise<AxiosResponse<IResponseTeamsMuted>> {
    return baseApi.post(`/teams/${id}/mute`, params)
  }

  getTeamByIdMembers(id: number): Promise<AxiosResponse<IResponseUser[]>> {
    // TODO optimize flow from V1
    return baseApi.get(`teams/${id}/members?limit=1000`)
  }

  getTeamByIdMembers2_0(
    id: number,
    params: { per_page: number; page: number }
  ): Promise<AxiosResponse<IResponseGetTeamByIdMembers2_0>> {
    return baseApi.get(`teams/${id}/members/list`, { params })
  }

  getInboxesTeam(id: number, params: { limit: number }) {
    return baseApi.get(`teams/${id}/members`, { params })
  }

  updateTeamsByIdMembers(
    inboxId: number,
    params: IParamsUpdateTeamsByIdMembers
  ): Promise<AxiosResponse<IResponseUpdateTeamsByIdMembers>> {
    return baseApi.post(`teams/${inboxId}/members`, params)
  }

  getTeamsByIdQueueBySid(
    inboxId: number,
    calSid: string
  ): Promise<AxiosResponse<IResponseGetTeamsByIdQueueBySid>> {
    return baseApi.get(`teams/${inboxId}/queue/${calSid}`)
  }

  createInboxAndAssignNumber(data: IParamsAssignInbox): Promise<AxiosResponse<IResponseInbox>> {
    return baseApi.post('teams/unassigned', data)
  }

  createInbox(data: IParamsCreateInbox): Promise<AxiosResponse<IResponseInbox>> {
    return baseApi.post('teams', data)
  }

  updateInboxesEmail(
    params: IParamsUpdateInboxesEmail
  ): Promise<AxiosResponse<IResponseUpdateInboxesEmail>> {
    return baseApi.put('teams/update-emails-to-sms', params)
  }

  makeDefaultInbox(id: number): Promise<AxiosResponse<IResponseMakeDefaultInbox>> {
    return baseApi.post(`teams/${id}/make-default`)
  }

  changeInboxOwner(
    id: number,
    params: IParamsChangeInboxOwner
  ): Promise<AxiosResponse<IResponseUser[]>> {
    return baseApi.put(`teams/${id}/change-owner`, params)
  }

  deleteMember(id: number, memberId: number): Promise<AxiosResponse<IResponseUser[]>> {
    return baseApi.delete(`teams/${id}/members/${memberId}`)
  }

  updateTeamByColorCoding(
    id: number,
    params: IParamsUpdateTeamsByColorCoding
  ): Promise<AxiosResponse<IResponseInbox>> {
    return baseApi.put(`teams/${id}/color-coding`, params)
  }

  updateTeamByHistory(
    id: number,
    params: IParamsUpdateTeamsByHistory
  ): Promise<AxiosResponse<IResponseInbox>> {
    return baseApi.post(`teams/${id}/conversation-history-settings`, params)
  }

  deleteInbox(id: number, params: IParamsDeleteInbox): Promise<AxiosResponse<null>> {
    return baseApi.delete(`teams/${id}`, { data: params })
  }

  deleteMembersBulk(
    id: number,
    members: number[]
  ): Promise<AxiosResponse<IResponseDeleteMembersBulk>> {
    return baseApi.delete(`teams/${id}/members/bulk`, { data: { members } })
  }

  getTeamByConversationAssignment(
    id: number
  ): Promise<AxiosResponse<IResponseInboxConversationAssignment>> {
    return baseApi.get(`teams/${id}/assigment-settings`)
  }

  updateTeamByConversationAssignment(
    id: number,
    params: IParamsUpdateTeamByConversationAssignment
  ): Promise<AxiosResponse<IResponseInboxConversationAssignment>> {
    return baseApi.post(`teams/${id}/assigment-settings`, params)
  }

  getHubspotAssignmentFields(
    id: number
  ): Promise<AxiosResponse<IResponseHubspotAssignmentFields[]>> {
    return baseApi.get(`teams/${id}/assigment-settings/hubspot-fields`)
  }

  updateInboxAiAgent(
    id: number,
    params: IParamsUpdateInboxAiAgent
  ): Promise<AxiosResponse<IResponseInbox>> {
    return baseApi.patch(`teams/${id}`, params)
  }

  updateInboxBusinessHours(
    params: IParamsUpdateInboxBusinessHours
  ): Promise<AxiosResponse<IResponseBusinessHours>> {
    return baseApi.post('business-hours', params)
  }

  updateInboxCallSettings(
    id: number,
    params: IParamsUpdateInboxCallSettings
  ): Promise<AxiosResponse<IResponseInbox>> {
    return baseApi.put(`teams/${id}/call-settings`, params)
  }

  updateInboxOutboundCallsAircall(
    id: number,
    params: { is_active: boolean }
  ): Promise<AxiosResponse<ICallSettingsItem>> {
    return baseApi.post(`teams/${id}/call-settings/call-via-aircall`, params)
  }

  updateInboxDeprioritizedNumbers(
    id: number,
    params: { deprioritized_numbers: number[] }
  ): Promise<AxiosResponse<IResponseInbox>> {
    return baseApi.post(`teams/${id}/numbers/deprioritize`, params)
  }

  updateInboxGeomatch(
    id: number,
    params: { is_geomatch: boolean }
  ): Promise<AxiosResponse<IResponseInbox>> {
    return baseApi.put(`teams/${id}/geo-match`, params)
  }

  addMembersToExistingInbox(
    id: number,
    params: { teams: { id: number }[] }
  ): Promise<AxiosResponse<IResponseAddMembersToExistingInbox>> {
    return baseApi.post(`teams/members/${id}`, params)
  }
}

export const InboxesApi = new Api()
